import type { ReactElement } from 'react';
import { useEffect, useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Navigate, useSearchParams } from 'react-router';
import { calendarStatus } from '@contracts/calendar';
import { API_ORIGIN, api, isNotSignedIn } from '../api/client';
import { keys, useMe } from '../api/queries';
import { Button, Card } from '../ui';
import { LEGAL_ORIGIN } from '../lib/legal';

/**
 * Board 16 — two steps, then a promise with dates on it. Nothing to download;
 * one Google consent, which is also the calendar's.
 */
export function Start(): ReactElement {
  const [params] = useSearchParams();
  const returnTo = params.get('returnTo') ?? '/';
  const meQuery = useMe();
  const client = useQueryClient();
  const connect = useMutation({
    mutationFn: () => api(calendarStatus, '/v1/calendar/connect', { method: 'POST' }),
    onSuccess: () => client.invalidateQueries({ queryKey: keys.me }),
  });

  const signedIn = meQuery.isSuccess;
  const me = meQuery.data;
  // A connector's consent page lives on the api's host; after signing in here,
  // the browser goes back to it. Only that one absolute address is followed —
  // anything else is the open redirect this page must not be. (It moved under
  // /v1 on 2026-09-23; the old form is still accepted for a request that was
  // already on its way.)
  const toConnector =
    API_ORIGIN !== '' &&
    (returnTo.startsWith(`${API_ORIGIN}/v1/oauth/authorize?`) ||
      returnTo.startsWith(`${API_ORIGIN}/oauth/authorize?`));
  const [stuck, setStuck] = useState(false);
  // Once per visit: a second run of the effect is React re-checking, not a
  // second bounce, and must not be mistaken for one.
  const sent = useRef(false);
  useEffect(() => {
    if (!signedIn || !toConnector || sent.current) return;
    sent.current = true;
    // ⚠ Never the same bounce twice in a row. The consent page sends a browser
    // it thinks is signed out here, and this page sends a signed-in one back:
    // if the two ever disagree about the session, they reload each other for
    // ever — which is exactly what the consent page did until 2026-09-23. A
    // retry from the app that asked carries a new `state`, so it is a new
    // address and goes through.
    if (bouncedJustNow(returnTo)) setStuck(true);
    else window.location.replace(returnTo);
  }, [signedIn, toConnector, returnTo]);
  if (signedIn && toConnector && stuck)
    return (
      <div className="page" style={{ maxWidth: 560 }}>
        <Card>
          <div className="stack">
            <span className="t-heading">You are signed in, but the connection did not see it.</span>
            <span className="t-secondary">
              Nothing was connected. Start the connection again from the app that asked — if it
              lands here a second time, tell us: that is a fault on our side, not yours.
            </span>
          </div>
        </Card>
      </div>
    );
  if (signedIn && toConnector)
    return <p className="page t-secondary">Back to the app that asked…</p>;
  if (signedIn && me?.calendar.connected)
    return <Navigate to={returnTo.startsWith('/') ? returnTo : '/'} replace />;
  const cancelled = params.get('signin') === 'cancelled';
  const expired = params.get('signin') === 'expired';
  const personal = params.get('signin') === 'personal';

  return (
    <div className="page" style={{ maxWidth: 560 }}>
      <div className="stack-lg">
        <div className="stack">
          <h1 className="t-title">
            Ptitim keeps the running account of what people owe each other.
          </h1>
          <p className="t-body muted">
            Two steps. Nothing to download, no extension to install — one Google sign-in, which is
            also the calendar's.
          </p>
        </div>

        <Card>
          <ol className="stack-lg">
            <li className="between">
              <div className="stack" style={{ gap: 2 }}>
                <span className="eyebrow">Step 1</span>
                <span className="t-heading">
                  {signedIn ? `Signed in as ${me?.email}` : 'Sign in'}
                </span>
                {cancelled ? <span className="t-secondary red">Sign-in was cancelled.</span> : null}
                {expired ? (
                  <span className="t-secondary red">That sign-in took too long — try again.</span>
                ) : null}
                {personal ? (
                  <span className="t-secondary red" role="alert">
                    Ptitim is for teams: sign in with your work Google account. A personal address —
                    Gmail, Outlook, iCloud and the like — cannot make an account.
                  </span>
                ) : null}
              </div>
              {signedIn ? (
                <span className="pill pill-green">✓ done</span>
              ) : (
                <a
                  className="btn btn-primary btn-lg"
                  href={`${API_ORIGIN}/v1/auth/google/start?returnTo=${encodeURIComponent('/start?returnTo=' + returnTo)}`}
                >
                  Continue with Google
                </a>
              )}
            </li>
            <li className="between">
              <div className="stack" style={{ gap: 2 }}>
                <span className="eyebrow">Step 2</span>
                <span className="t-heading">Connect the calendar</span>
                <span className="t-secondary">
                  Meetings start appearing, and the notetaker joins the ones with a link — nobody
                  presses anything.
                </span>
              </div>
              {!signedIn ? (
                <span className="pill">after step 1</span>
              ) : me?.calendar.grantReady ? (
                <Button
                  variant="primary"
                  size="lg"
                  onClick={() => connect.mutate()}
                  working={connect.isPending ? 'Connecting…' : false}
                >
                  Connect
                </Button>
              ) : (
                <a
                  className="btn btn-primary btn-lg"
                  href={`${API_ORIGIN}/v1/auth/google/start?calendar=1&returnTo=${encodeURIComponent('/start?returnTo=' + returnTo)}`}
                >
                  Allow calendar access
                </a>
              )}
            </li>
          </ol>
          {connect.isError ? (
            <p className="t-secondary red" style={{ marginTop: 12 }}>
              {isNotSignedIn(connect.error) ? 'Sign in first.' : connect.error.message}
            </p>
          ) : null}
        </Card>

        <p className="t-secondary">
          The notetaker joins as a guest under your name, says in the call that it is recording, and
          leaves when everyone has. What it heard belongs to the people who were there, and to
          nobody else until it is shared.
        </p>
        {/* Beside the button, not in a footer: agreeing has to be something a person could
            have seen (GDPR checklist A4). The legal pages live on the landing's host. */}
        <p className="t-secondary">
          By continuing you agree to the <a href={`${LEGAL_ORIGIN}/terms`}>Terms</a> — including
          that recording other people lawfully is your responsibility — and confirm you have read
          the <a href={`${LEGAL_ORIGIN}/privacy`}>Privacy</a> page.
        </p>
      </div>
    </div>
  );
}

/**
 * Whether this page sent the browser to this same consent address a moment
 * ago — the sign that the two are bouncing it between them. Remembered for the
 * tab only; where storage is refused, it answers "no", which is how this page
 * behaved before the guard existed.
 */
function bouncedJustNow(to: string): boolean {
  const KEY = 'ptitim.connector-bounce';
  try {
    const last = JSON.parse(sessionStorage.getItem(KEY) ?? 'null') as {
      to: string;
      at: number;
    } | null;
    if (last && last.to === to && Date.now() - last.at < 30_000) return true;
    sessionStorage.setItem(KEY, JSON.stringify({ to, at: Date.now() }));
  } catch {
    // Private windows and blocked storage: bounce as before.
  }
  return false;
}
